import { getTierConfig, getTierRangeForGroup, niveauCodeToTier } from '../content';
import { GroupId } from '../config/systemConfig';
export interface CitoLevel {
  level: 'I' | 'II' | 'III' | 'IV' | 'V';
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}
const CITO_COLORS = {
  I: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  },
  II: {
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200'
  },
  III: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  IV: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  V: {
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200'
  }
};
function buildCitoLevel(level: 'I' | 'II' | 'III' | 'IV' | 'V'): CitoLevel {
  return {
    level,
    label: `CITO ${level}`,
    ...CITO_COLORS[level]
  };
}

/**
 * Maps a tier to a CITO level (I-V) relative to the student's group.
 *
 * R3 fixes (Assessment audit 2026):
 * - F-AS-R3-CITO1: Group 8 now reaches CITO V at the bottom of its in-range
 *   tiers (B8 → V) instead of always returning IV.
 * - F-AS-R3-CITO2: M+ tiers (groups 6/7) now map to II instead of collapsing
 *   into III alongside M. Reduces signal loss in mid-year band.
 *
 * @param tier The canonical tier number (1-16)
 * @param group The student's group (6, 7, 8)
 */
export function tierToCitoLevel(tier: number, group: GroupId): CitoLevel {
  const range = getTierRangeForGroup(group);

  // Cross-group handling: tier outside the expected range for the group
  if (tier < range.min) return buildCitoLevel('V');
  if (tier > range.max) return buildCitoLevel('I');
  const config = getTierConfig(tier);
  if (!config) return buildCitoLevel('III');
  let level: 'I' | 'II' | 'III' | 'IV' | 'V' = 'III';
  if (group === 6 || group === 7) {
    // 6 sub-periods (B, B+, M, M+, E, E+) compressed into 5 CITO levels.
    // R3-CITO2: M+ now maps to II (was III, collapsing with M).
    if (config.period === 'B' && !config.isPlus) level = 'V';else if (config.period === 'B' && config.isPlus) level = 'IV';else if (config.period === 'M' && !config.isPlus) level = 'III';else if (config.period === 'M' && config.isPlus) level = 'II';else if (config.period === 'E' && !config.isPlus) level = 'II';else if (config.period === 'E' && config.isPlus) level = 'I';
  } else if (group === 8) {
    // Group 8 has 4 in-range tiers (B8, M8, E8, E8+).
    // R3-CITO1: B8 now maps to V (was IV) so group 8 students can land on
    // every CITO band relative to their cohort. The trade-off: there is no
    // distinct CITO IV inside group 8; the bottom band is V, which matches
    // the convention that a group-8 child still at "Begin Groep 8" is
    // significantly below the year-curve.
    if (config.period === 'B') level = 'V';else if (config.period === 'M') level = 'IV';else if (config.period === 'E' && !config.isPlus) level = 'II';else if (config.period === 'E' && config.isPlus) level = 'I';
  }
  return buildCitoLevel(level);
}

/**
 * Convenience wrapper to map a level code (e.g. 'M7+') to a CITO level.
 */
export function levelCodeToCitoLevel(levelCode: string, group: GroupId): CitoLevel | null {
  const tier = niveauCodeToTier(levelCode);
  if (tier === undefined) return null;
  return tierToCitoLevel(tier, group);
}